import { styled } from "@mui/material/styles";
import { Stack, useTheme, useMediaQuery, Typography, Box } from "@mui/material";
import { useShow } from "hooks/useShow";
import Grid from "@mui/material/Unstable_Grid2";
import fe1 from "assets/images/fe/fe-1.svg";
import fe2 from "assets/images/fe/fe-2.svg";
import fe3 from "assets/images/fe/fe-3.svg";
import fe4 from "assets/images/fe/fe-4.svg";
import fe5 from "assets/images/fe/fe-5.svg";
import fe6 from "assets/images/fe/fe-6.svg";
import fe7 from "assets/images/fe/fe-7.svg";
import fe8 from "assets/images/fe/fe-8.svg";
import Transitions from "components/Transitions";
import { useEffect, useRef } from "react";
import { gsap } from "gsap";

const WapperBox = styled(Stack)`
  ${({ theme }) => ({
    [theme.breakpoints.down("md")]: {},
  })};
`;

const Head = styled(Typography)`
  color: #fff;
  font-size: 46px;
  font-weight: 500;
  max-width: 650px;
  margin-top: 30px;
  line-height: 1.2;
  ${({ theme }) => ({
    [theme.breakpoints.down("md")]: {
      fontSize: "24px",
      marginTop: "10px",
    },
  })};
`;

const SubHead = styled(Typography)`
  color: #999;
  font-size: 18px;
  margin-top: 5px;
  font-family: KindSansRegular;
  ${({ theme }) => ({
    [theme.breakpoints.down("md")]: {
      fontSize: "18px",
    },
  })};
`;

const Containter = styled(Stack)`
  height: 100%;
  width: 1200px;
  padding: 15px 25px 140px;
  box-sizing: border-box;
  ${({ theme }) => ({
    [theme.breakpoints.down("md")]: {
      width: "100%",
      padding: "0px 20px 60px",
    },
  })};
`;

const GridItem = styled(Stack)`
  aspect-ratio: 1 / 1;
  border-radius: 20px;
  border: 1px solid #3f3f3f;
  background: #191919;
  box-shadow: 0px 0px 4px 0px rgba(0, 0, 0, 0.12);
  opacity: 0;
  &:hover {
    background: linear-gradient(180deg, #1f7a55 0%, #1c332a 100%);
    background-blend-mode: color-dodge, normal;
    box-shadow: 0px 1px 0px 0px rgba(255, 255, 255, 0.25) inset,
      -1px -1px 0px 0px rgba(0, 0, 0, 0.25) inset;
  }
  ${({ theme }) => ({
    [theme.breakpoints.down("md")]: {
      aspectRatio: "1.8/1",
    },
  })};
`;

const ByImg = styled("img")`
  height: auto;
  width: 32px;
`;

const Title = styled(Typography)`
  color: #fff;
  font-size: 18px;
  margin-top: 20px;
  ${({ theme }) => ({
    [theme.breakpoints.down("md")]: {
      fontSize: "18px",
    },
  })};
`;

const Desc = styled(Typography)`
  color: #999;
  font-size: 14px;
  margin-top: 16px;
  font-family: KindSansRegular;
  ${({ theme }) => ({
    [theme.breakpoints.down("md")]: {
      fontSize: "12px",
      marginTop: "12px",
    },
  })};
`;

const Features = () => {
  const theme = useTheme();
  const upToMd = useMediaQuery(theme.breakpoints.up("md"));
  const { ref, isShow } = useShow();
  const itemBoxRefs = useRef<(HTMLDivElement | null)[]>([]);
  const feList = [
    {
      url: fe2,
      title: "Fully collateralized & secure",
      desc: "USD0x is 100% collateralized with T-bills, cash, and cash equivalents, ensuring financial stability and security. All collateral is securely held by trusted and long-established banking partners.",
    },
    {
      url: fe3,
      title: "Bankruptcy remote",
      desc: "Built to be bankruptsy-remote, protecting investors' assets from unforeseen circumstances.",
    },
    {
      url: fe4,
      title: "Liquid & cash-like",
      desc: "It functions similarly to cash in various real-world scenarios, providing a superior alternative to cash, tokens, and T-bills in certain contexts.",
    },
    {
      url: fe5,
      title: "Interoperable & mirrorable",
      desc: "USD0x bridges CeFi, DeFi, and TradFi, enabling seamless asset interoperability and cross-platform liquidity, maximizing capital efficiency and liquidity management.",
    },
    {
      url: fe6,
      title: "On-chain yield",
      desc: "USD0x integrates seamlessly with smart contracts, supporting on-chain lending, trading, derivatives, and more.",
    },
    {
      url: fe1,
      title: "Access T-bill returns",
      desc: "Each USD0x is fully backed by $1 worth of T-bills. Through our ecosystem partnerships, users may access opportunities linked to the returns generated by these high-quality.",
    },
    {
      url: fe7,
      title: "Next gen collateral",
      desc: "USD0x will be recognized as a collateral asset, enabling users to borrow other on-chain assets.",
    },
    {
      url: fe8,
      title: "Institutional applications",
      desc: "Remittance, global transaction (such as commodity trading), primary market fundraising.",
    },
  ];
  // GSAP 动画
  useEffect(() => {
    if (isShow) {
      gsap.to(itemBoxRefs.current, {
        opacity: 1, // 淡入
        y: 0, // 从下往上移动
        stagger: 0.15, // 每个 ItemBox 的动画间隔 0.2 秒
        duration: 0.6, // 动画持续时间
        ease: "power2.out", // 缓动函数
      });
    }
  }, [isShow]);
  return (
    <WapperBox justifyContent="center" alignItems="center" direction="row">
      <Containter ref={ref}>
        <Stack>
          <Transitions delay={200} isShow={isShow}>
            <SubHead>Features & Utility</SubHead>
          </Transitions>
          <Transitions delay={400} isShow={isShow}>
            <Head>From Yield to Liquidity, Seamless Payment Redefined</Head>
          </Transitions>
        </Stack>
        <Stack mt={{ xs: 30, md: 100 }}>
          <Grid wrap="wrap" container spacing={{ xs: 20, md: 10 }}>
            {feList.map((item, index) => (
              <Grid key={index} xs={12} md={4} lg={3}>
                <GridItem
                  justifyContent="flex-start"
                  alignItems="flex-start"
                  direction="column"
                  width={"100%"}
                  height={"100%"}
                  ref={(el) => (itemBoxRefs.current[index] = el)}
                >
                  <Stack p={24}>
                    <ByImg src={item?.url} />
                    <Title>{item?.title}</Title>
                    <Desc>{item?.desc}</Desc>
                  </Stack>
                </GridItem>
              </Grid>
            ))}
          </Grid>
        </Stack>
      </Containter>
    </WapperBox>
  );
};

export default Features;
